import React, {FunctionComponent, useEffect, useMemo, useState} from "react";
import {RootState} from "../../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {GenerationEntity, getEntities} from "./GenerationEnergySkidChartReducer";
import IconButton from "@mui/material/IconButton";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {format} from "date-fns";
import {ptBR} from "date-fns/locale";
import {isArray} from "lodash";
import {
    ACard,
    AChartX,
    Period,
    PeriodSelector,
    Skeleton,
    XAxis,
    YAxis
} from "@atiautomacao/ati-ui-library"
import {useSnackbar} from "notistack";
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {Box, Divider, Stack, Typography, useTheme} from "@mui/material";
import { SimCardDownloadOutlined } from "@mui/icons-material";
import {
    buildAxisTooltipFormatter,
    buildOverlappedBarSeries,
    ChartValue,
    OverlappedBarSeries
} from "../../../../Utils/OverlappedBarSeriesUtils";

interface LineSeriesOption {
    name: string;
    type: "line";
    smooth: boolean;
    symbol: string;
    z: number;
    itemStyle: { color: string };
    lineStyle: { color: string; width: number; type?: string };
    tooltip: { valueFormatter: (value: number) => string };
    yAxisIndex: number;
    data: ChartValue[];
}

type SeriesOption = OverlappedBarSeries | LineSeriesOption;


const ENERGY_UNIT = "MWh";
const IRRADIANCE_SERIES_NAME = "Irradiação";
const EXPECTED_IRRADIANCE_SERIES_NAME = "Irradiação Esperada";
// Linhas acima das barras sobrepostas (camadas usam z 2..5).
const LINE_Z = 10;
const IRRADIANCE_COLOR = "#B7862C";
const EXPECTED_IRRADIANCE_COLOR = "#C9B65A";

// Ordem e cores iguais às da Visão Usina; define também a ordem na legenda e no tooltip.
const ENERGY_SERIES: Array<{ name: string; color: string; getValue: (item: GenerationEntity) => number | undefined | null }> = [
    {name: "Energia Esp. Corrigida", color: "#C5D8EE", getValue: (item) => item.expectedEnergyCorrectedByIrradiation},
    {name: "Energia Esperada", color: "#4A6FA5", getValue: (item) => item.expectedInjectedEnergy},
    {name: "Energia Gerada", color: "#2D5DE7", getValue: (item) => item.generatedEnergy},
    {name: "Energia Injetada", color: "#1BB483", getValue: (item) => item.injectedEnergy},
];

// Mantém o comportamento atual: ausência (e zero) vira '-'.
const toChartValue = (value: number | undefined | null): ChartValue => value ? truncateNumber(value, 2) : "-";

const resolveIrradianceUnit = (groupBy: string): "kWh/m²" | "Wh/m²" =>
    groupBy === "year" || groupBy === "general" ? "kWh/m²" : "Wh/m²";
const GenerationEnergySkidChart: FunctionComponent<{powerPlant: any, skid: any}> = ({powerPlant, skid}) =>  {

    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const generation: Array<GenerationEntity> = useAppSelector((state: RootState) => state.generationEnergySkid.entities);
    const { loading, errorMessage } = useAppSelector((state: RootState) => state.generationEnergySkid);
    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useAppDispatch();
    const theme = useTheme();
    // Setting range start period
    const initialPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const [period, setPeriod] = useState<Period>(initialPeriod);
    const [series, setSeries] = useState<Array<SeriesOption>>();
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    
    const maxEnergy = () => {
        const maxGeneratedEnergy= Array.isArray(generation) ? Math.max(...generation.map(item => item.generatedEnergy)) : 0
        const maxInjectedEnergy = Array.isArray(generation) ? Math.max(...generation.map(item => item.injectedEnergy)) : 0
        const maxExpectedEnergy = Array.isArray(generation) ? Math.max(...generation.map(item => item.expectedInjectedEnergy)) : 0
        const maxExpectedEnergyCorrectedByIrradiation = Array.isArray(generation) ? Math.max(...generation.map(item => item.expectedEnergyCorrectedByIrradiation)) : 0

        const valueMax = Math.max(maxGeneratedEnergy, maxInjectedEnergy, maxExpectedEnergy, maxExpectedEnergyCorrectedByIrradiation);
        return valueMax < 0 ? 0 : truncateNumber(valueMax + (valueMax * 0.15)); // Adding 15% margin
    }

     const maxIrradiance = () => {
        const maxGeneratedIrradiance= Array.isArray(generation) ? Math.max(...generation.map(item => item.irradiance)) : 0
        const maxIrradiance = Array.isArray(generation) ? Math.max(...generation.map(item => item.expectedIrradiance)) : 0

        const valueMax = Math.max(maxGeneratedIrradiance, maxIrradiance);
        return valueMax < 0 ? 0 : truncateNumber(valueMax + (valueMax * 0.15)); // Adding 15% margin
    }

    function findGeneratedEnergyDataByPeriod() {

        let xAxisName = "Dia";
        let datePeriod: string[] = [];
        let nameGap = 58

        if (period.groupBy.valueOf() === "week" || period.groupBy.valueOf() === "month" || period.groupBy.valueOf() === 'none') {
            datePeriod = generation ? generation
                .map(item => format(new Date(item.dateTime+"T00:00:00"), "dd/MM")) : [];
            nameGap = 35
        } else if (period.groupBy.valueOf() === "year") {
            xAxisName = 'Mês';
            datePeriod = Array.isArray(generation) ? generation
                .map(item => format(new Date(item.dateTime+"T00:00:00"), "MMM", {locale: ptBR})) : []
            nameGap = 48
        } else if (period.groupBy.valueOf() === "general") {
            datePeriod = Array.isArray(generation) ? generation.map(item => format(new Date(item.dateTime+"T00:00:00"), "yyyy")) : [];
            xAxisName = 'Ano';
        }

        setXAxis({
            name: xAxisName,
            data: datePeriod,
            nameLocation: "middle",
            nameGap: 25
        });
        const maxYAxisLeft = maxEnergy();
        const maxYAxisRight = maxIrradiance();
        setYAxis([
            {
                name: 'MWh',
                max: maxYAxisLeft,
                min: 0,
                interval: truncateNumber(maxYAxisLeft / 4),
                nameGap: nameGap,
                nameLocation: "middle"
            }, {
                name: resolveIrradianceUnit(period.groupBy.valueOf()),
                max: maxYAxisRight,
                min: 0,
                interval: truncateNumber(maxYAxisRight / 4),
                nameGap: period.groupBy.valueOf() === "year" ? nameGap : 70,
                nameLocation: "middle"
            }
        ])

        const irradianceUnit = resolveIrradianceUnit(period.groupBy.valueOf());
        const data = Array.isArray(generation) ? generation : [];

        // As 4 energias numa mesma barra por período, maior ao fundo e menor à frente.
        const energyBars = buildOverlappedBarSeries(ENERGY_SERIES.map((energy) => ({
            name: energy.name,
            color: energy.color,
            values: data.map((item) => toChartValue(energy.getValue(item))),
        })));

        const expectedIrradiance: LineSeriesOption = {
            name: EXPECTED_IRRADIANCE_SERIES_NAME,
            type: 'line',
            smooth: true,
            symbol: 'emptyCircle',
            z: LINE_Z,
            itemStyle: {color: EXPECTED_IRRADIANCE_COLOR},
            lineStyle: {color: EXPECTED_IRRADIANCE_COLOR, width: 2, type: 'dashed'},
            tooltip: {valueFormatter: (value: number) => `${value} ${irradianceUnit}`},
            yAxisIndex: 1,
            data: data.map((item) => toChartValue(item.expectedIrradiance)),
        };

        const irradiance: LineSeriesOption = {
            name: IRRADIANCE_SERIES_NAME,
            type: 'line',
            smooth: true,
            symbol: 'emptyCircle',
            z: LINE_Z,
            itemStyle: {color: IRRADIANCE_COLOR},
            lineStyle: {color: IRRADIANCE_COLOR, width: 2},
            tooltip: {valueFormatter: (value: number) => `${value} ${irradianceUnit}`},
            yAxisIndex: 1,
            data: data.map((item) => toChartValue(item.irradiance)),
        };

        setSeries([...energyBars, expectedIrradiance, irradiance]);
    }
    useEffect(() => {
        if(checkDiffDays(period.toDateTime, period.fromDateTime)){
            dispatch(getEntities({period, powerPlant, skid}));
        }
    }, [period, powerPlant, skid, dispatch]);

    useInterval(() => {
        if(checkDiffDays(period.toDateTime, period.fromDateTime)){
            dispatch(getEntities({period, powerPlant, skid}));
        }
    }, userConfig.mainUpdateInterval) // 5 minutes

    useEffect(() => {
        if(isArray(generation) && generation.length > 0){
            findGeneratedEnergyDataByPeriod();
        }else {
            setSeries([]);
            setXAxis(undefined);
        }
    }, [generation]);


    const tooltipFormatter = useMemo(() => {
        const irradianceUnit = resolveIrradianceUnit(period.groupBy.valueOf());
        return buildAxisTooltipFormatter([
            ...ENERGY_SERIES.map((energy) => ({name: energy.name, unit: ENERGY_UNIT})),
            {name: EXPECTED_IRRADIANCE_SERIES_NAME, unit: irradianceUnit},
            {name: IRRADIANCE_SERIES_NAME, unit: irradianceUnit},
        ]);
    }, [period.groupBy]);

    // @ts-ignore
    const option: AChartXProps['option'] = {
        color: ['#5470C6', '#E56C6C'],
        tooltip: {
            trigger: 'axis',
            formatter: tooltipFormatter,
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            }
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: {
                    show: true,
                    name: `energia-gerada-${format(new Date(new Date()), 'yyyy-MM-dd')}`,
                },
            },
        },
        xAxis: xAxis,
        yAxis: yAxis,
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal', // Alterado para horizontal
            left: 'center',        // Centraliza a legenda
            bottom: 3,            // Coloca a legenda na parte inferior
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            // As camadas de uma energia repetem o name; a legenda mostra um item por série.
            data: Array.from(new Set((series ?? []).map((serie) => serie.name)))
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 45,
            right: 50,
            left: 32,
        },
        label: {
            precision: 1,
            position: 'right',
            valueAnimation: true,
            fontFamily: 'monospace'
        },
        animationDuration: 2000,
        series: series
    };

    useEffect(() => {
        if(errorMessage) {
            enqueueSnackbar(errorMessage, {variant: 'error'})
        }
    }, [errorMessage]);

    return (
        <>
            <ACard
                key={`chart-skid-energy`}
                headerControlsPosition={"header"}
                styleProps={{
                    cardStyle: { height: 463 },
                    contentStyle: { padding: '0 16px', height: 408 },
                    subTitleStyle: { display: 'none'},
                }}
                title={"Energia Gerada"}
                ariaLabel="dashboard-skid-generation-energy-chart"
                headerControls={

                        <PeriodSelector
                            styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                            mode="hybrid"
                            hideDatetime={false}
                            hideGroup={false}
                            disableHourSelector={true}
                            inputFormat={'dd/MM/yyyy'}
                            periodInterval={{
                                week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6},
                                month: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 30},
                                year: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 11},
                                general: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6}
                            }}
                            disabled={loading}
                            period={period}
                            onChange={setPeriod}
                            groupByOptions={["week", "month", "year", "general"]}
                        />
                }
                headerActions={
                    <Stack direction="column" spacing={"8px"}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                            <IconButton disabled={true} size="small" >
                                <SimCardDownloadOutlined sx={{ marginRight: '12px' }} />
                                <Typography variant="body2">Baixar como CSV</Typography>

                            </IconButton>
                        </Box>
                        <Divider />
                    </Stack>
                }
            >
                {
                    loading ?
                        <Skeleton animation="wave" height={363} variant="rounded" width={"100%"} />
                        :
                        series && xAxis && yAxis ?
                            <AChartX
                                key={`chart-skid-energy-${openSubMenu}`}
                                option={option}
                                height={363}
                                width={"100%"}
                                loading={false}
                                theme={theme.palette.mode}
                            />
                            :
                            <DataNotFound boxStyle={{height: 363, width: '100%'}}/>
                }
            </ACard>
        </>
    );
}
export default GenerationEnergySkidChart;
